/* eslint-disable no-unused-expressions */
/* eslint-disable max-lines */

import {
  PostTestSentencePayloadV2,
  clearTestSentence,
  postTestSentenceV2,
  replaceURLParams,
  showErrorAlert,
} from 'actions';
import { setCurrentTestIdentifierRevisionId } from 'actions/identifier';
import { setShowHighlights } from 'actions/ruleGroup';
import LoadingIndicator from 'components/LoadingIndicator';
import SelectRedesign from 'components/Select/SelectRedesign';
import SentencesViolatedGlobalSidebarSection from 'components/SentencesViolatedSection/SentencesViolatedGlobalSidebarSection';
import keyMap from 'constants/configHotKeys';
import { EXPAND_ICON } from 'constants/dashboardIcons';
import React, { useRef, useState } from 'react';
import { GlobalHotKeys } from 'react-hotkeys';
import { useDispatch } from 'react-redux';
import {
  getAnnotatedCategorizerTestSentenceResult,
  getAnnotationMatchedLanguageV2,
  getCommunicationTest,
  getMatchedLanguageByRevision,
  getShowV2Graph,
} from 'selectors/communications';
import {
  getCurrentTestIdentifierRevisionId,
  getIdentifierCustomers,
  getProductionIdentifierRevision,
  getSelectedIdentifierRevision,
} from 'selectors/identifier';
import { getNavParams } from 'selectors/nav';
import { getPreviewRuleGroupLoading } from 'selectors/ruleGroup';
import { useSelector } from 'store';
import logEvent from 'utils/analytics';
import { v4 } from 'uuid';

type ComponentProps = {
  testPhrase?: string;
  identifierId?: string;
};

const TestSentenceVisualizerSidebarCategorizer: React.FC<ComponentProps> = ({
  testPhrase,
  identifierId,
}) => {
  const { useEffect } = React;
  const dispatch = useDispatch();
  const testSentenceLoading = useSelector(getPreviewRuleGroupLoading);

  const annotationsMatchedV2 = useSelector(getAnnotationMatchedLanguageV2);
  const chainResults = useSelector(getMatchedLanguageByRevision);

  const communicationTest = useSelector(getCommunicationTest);

  const bodyParam = useSelector(getNavParams).body || '';

  const currentTestIdentifierId = useSelector(getCurrentTestIdentifierRevisionId);

  const identifierRevision = useSelector(getSelectedIdentifierRevision);
  const prodRevision = useSelector(getProductionIdentifierRevision);

  const showV2 = useSelector(getShowV2Graph);
  const customers = useSelector(getIdentifierCustomers);

  const [body, setBody] = useState<string>(bodyParam as string);
  const [debouncedBody, setDebouncedBody] = useState('');
  const [firstTime, setFirstTime] = useState(true);
  const [showResults, setShowResults] = useState(false);

  const [selectedCustomerTest, setSelectedCustomerTest] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedEnv, setSelectedEnv] = useState<'staging' | 'production'>('staging');

  const selectedRevisionId =
    selectedEnv === 'staging' ? identifierRevision?.uuid : prodRevision?.uuid;

  const resultLinesV2 = useSelector((state) =>
    getAnnotatedCategorizerTestSentenceResult(state, selectedCategory, selectedRevisionId || '')
  );

  const [channelId] = useState(v4());

  const RunButtonRef = useRef<HTMLButtonElement>(null);
  const SaveButtonRef = useRef<HTMLButtonElement>(null);
  const TextAreaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const channel = new BroadcastChannel(`test-visualizer-channel-${channelId}`);
    if (communicationTest) {
      channel.postMessage({ communication: communicationTest });
    }
  }, [communicationTest, channelId]);

  useEffect(() => {
    const channel = new BroadcastChannel(`test-visualizer-channel-${channelId}`);

    const handleEvent = (event: MessageEvent<{ ready: boolean }>): void => {
      if (event.data.ready) {
        if (communicationTest) {
          channel.postMessage({ communication: communicationTest });
        }
      }
    };

    channel.addEventListener('message', handleEvent);

    return () => {
      channel.removeEventListener('message', handleEvent);
    };
  }, [communicationTest, channelId]);

  // works as a componentWillUnmount to avoid having data when returning to this view
  // @ts-ignore
  useEffect(() => {
    if (!currentTestIdentifierId) {
      dispatch(clearTestSentence());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTestIdentifierId]);

  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedBody(body), 500);
    return (): void => {
      clearTimeout(timeoutId);
    };
  }, [body]);

  useEffect(() => {
    if (bodyParam !== debouncedBody) {
      dispatch(replaceURLParams({ body: debouncedBody }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedBody, dispatch]);

  useEffect(() => {
    if (resultLinesV2) {
      setShowResults(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultLinesV2]);

  useEffect(() => {
    if (testPhrase) {
      setBody(testPhrase || '');
    }
  }, [testPhrase]);

  const submitTest = (useV2 = false): void => {
    if (!selectedCustomerTest) {
      dispatch(showErrorAlert('You need to select a customer to test.'));
      return;
    }
    const payload: PostTestSentencePayloadV2 = {
      body,
      platform: 'all',
      force_v2_graphs: useV2,
      ...(selectedCustomerTest === 'all' || !selectedCustomerTest
        ? {}
        : { customer_uuid: selectedCustomerTest }),
    };
    payload.identifiers = [{ uuid: identifierId || '' }, { uuid: prodRevision?.uuid || '' }];
    dispatch(postTestSentenceV2(payload));
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    logEvent('tests-run-test');

    submitTest(showV2);
  };

  useEffect(() => {
    if (!bodyParam) return;
    setBody(bodyParam as string);
    if (firstTime && selectedCustomerTest) {
      submitTest(true);
      setFirstTime(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyParam, firstTime]);

  const handlers = {
    RUN_TEST_PHRASE: (): void => {
      if (document.activeElement === TextAreaRef?.current) {
        RunButtonRef?.current?.click();
      }
    },
    RUN_TEST_PHRASE_ALT: (): void => {
      if (document.activeElement === TextAreaRef?.current) {
        RunButtonRef?.current?.click();
      }
    },
    SAVE_TO_SUITE: (event: KeyboardEvent | undefined): void => {
      if (event) {
        event.preventDefault();
        if (document.activeElement === TextAreaRef?.current) {
          SaveButtonRef?.current?.click();
        }
      }
    },
  };

  const customerOptions = [
    ...customers.map((cus) => ({
      label: cus.name || '',
      value: cus.uuid || '',
    })),
  ];

  const facets = (identifierRevision?.options_config?.categorization_facets || []) as string[];
  // @ts-ignore
  const thresholds = identifierRevision?.options_config?.customer_thresholds as Record<
    string,
    Record<string, string>
  >;
  const customerThresholds = thresholds?.[selectedCustomerTest];
  const categoryThreshold = customerThresholds?.[selectedCategory]?.toString() || '';
  const categoryMatch =
    chainResults?.[identifierRevision?.uuid || '']?.scores?.find(
      (score) => score.name === selectedCategory
    ) || null;

  // @ts-ignore
  const prodThresholds = prodRevision?.options_config?.customer_thresholds as Record<
    string,
    Record<string, string>
  >;
  const prodCustomerThresholds = prodThresholds?.[selectedCustomerTest];
  const prodCategoryThreshold = prodCustomerThresholds?.[selectedCategory]?.toString() || '';
  const prodCategoryMatch =
    chainResults?.[prodRevision?.uuid || '']?.scores?.find(
      (score) => score.name === selectedCategory
    ) || null;

  const categoryOptions =
    facets.map((fac) => ({
      label: fac || '',
      value: fac || '',
    })) || [];

  const handleSelectCategory = (value: string): void => {
    setSelectedCategory(value);
  };

  const handleEdit = (): void => {
    setShowResults(false);
    TextAreaRef.current?.focus();
  };

  const handleClear = (): void => {
    setShowResults(false);
    setBody('');
    setDebouncedBody('');
    TextAreaRef.current?.focus();
  };

  return (
    <div className="p-4">
      <GlobalHotKeys keyMap={keyMap} handlers={handlers} />
      <div className="flex flex-col gap-4 pb-5">
        <div className="flex flex-col gap-2 justify-start">
          <div className="flex flex-row gap-2 items-center">
            <span>Category</span>
            <div className="w-36">
              <SelectRedesign
                placeholder="Select"
                onChange={handleSelectCategory}
                options={categoryOptions}
                value={selectedCategory}
                dataTestid="user-categories"
              />
            </div>
          </div>
          <div className="flex flex-row gap-2 items-center">
            <span>on</span>
            <div className="w-36">
              <SelectRedesign
                placeholder="Customers"
                onChange={(option: string): void => {
                  setSelectedCustomerTest(option);
                }}
                options={customerOptions}
                value={selectedCustomerTest}
                dataTestid="user-customers"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3">
          {showResults && resultLinesV2 ? (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col">
                <button
                  type="button"
                  className={`py-1 px-1.5 flex flex-row gap-1 items-center  border-b border-litlingo-gray-0.5 text-small focus:outline-none
                  ${selectedEnv === 'staging' ? 'bg-litlingo-focus-area-background' : ''}
                  
                    `}
                  onClick={(): void => {
                    setSelectedEnv('staging');
                  }}
                >
                  <svg
                    width="11"
                    height="10"
                    viewBox="0 0 11 10"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2.26309 9.91604C2.32039 9.96937 2.39825 9.99955 2.47962 9.99999C2.52 10.0003 2.56003 9.99307 2.59726 9.97864C2.63449 9.9642 2.66814 9.9429 2.69614 9.91604L7.80173 5.20319C7.8304 5.17707 7.85317 5.14595 7.86871 5.11164C7.88425 5.07733 7.89225 5.04051 7.89225 5.00333C7.89225 4.96614 7.88425 4.92932 7.86871 4.89501C7.85317 4.8607 7.8304 4.82958 7.80173 4.80346L2.69614 0.0896109C2.66807 0.0619091 2.63424 0.0397035 2.59668 0.0243155C2.55912 0.00892748 2.51859 0.000671647 2.4775 3.93014e-05C2.43641 -0.000593044 2.39561 0.00641102 2.35751 0.0206347C2.31942 0.0348583 2.28481 0.0560109 2.25575 0.0828334C2.22669 0.109656 2.20378 0.1416 2.18837 0.176766C2.17296 0.211931 2.16537 0.249598 2.16606 0.287526C2.16674 0.325453 2.17569 0.362866 2.19236 0.397537C2.20903 0.432209 2.23308 0.46343 2.26309 0.489344L7.15216 5.00333L2.26309 9.51631C2.23455 9.5425 2.2119 9.57364 2.19644 9.60794C2.18099 9.64224 2.17303 9.67903 2.17303 9.71618C2.17303 9.75333 2.18099 9.79011 2.19644 9.82441C2.2119 9.85871 2.23455 9.88985 2.26309 9.91604Z"
                      fill="#333333"
                    />
                  </svg>
                  Staging matches
                </button>
                <button
                  type="button"
                  className={`py-1 px-1.5 flex flex-row gap-1 items-center border-b border-litlingo-gray-0.5 text-small focus:outline-none
                  ${selectedEnv === 'production' ? 'bg-litlingo-focus-area-background' : ''}
                  `}
                  onClick={(): void => {
                    setSelectedEnv('production');
                  }}
                >
                  <svg
                    width="11"
                    height="10"
                    viewBox="0 0 11 10"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2.26309 9.91604C2.32039 9.96937 2.39825 9.99955 2.47962 9.99999C2.52 10.0003 2.56003 9.99307 2.59726 9.97864C2.63449 9.9642 2.66814 9.9429 2.69614 9.91604L7.80173 5.20319C7.8304 5.17707 7.85317 5.14595 7.86871 5.11164C7.88425 5.07733 7.89225 5.04051 7.89225 5.00333C7.89225 4.96614 7.88425 4.92932 7.86871 4.89501C7.85317 4.8607 7.8304 4.82958 7.80173 4.80346L2.69614 0.0896109C2.66807 0.0619091 2.63424 0.0397035 2.59668 0.0243155C2.55912 0.00892748 2.51859 0.000671647 2.4775 3.93014e-05C2.43641 -0.000593044 2.39561 0.00641102 2.35751 0.0206347C2.31942 0.0348583 2.28481 0.0560109 2.25575 0.0828334C2.22669 0.109656 2.20378 0.1416 2.18837 0.176766C2.17296 0.211931 2.16537 0.249598 2.16606 0.287526C2.16674 0.325453 2.17569 0.362866 2.19236 0.397537C2.20903 0.432209 2.23308 0.46343 2.26309 0.489344L7.15216 5.00333L2.26309 9.51631C2.23455 9.5425 2.2119 9.57364 2.19644 9.60794C2.18099 9.64224 2.17303 9.67903 2.17303 9.71618C2.17303 9.75333 2.18099 9.79011 2.19644 9.82441C2.2119 9.85871 2.23455 9.88985 2.26309 9.91604Z"
                      fill="#333333"
                    />
                  </svg>{' '}
                  Production matches
                </button>
              </div>
              <div>matches</div>
              <SentencesViolatedGlobalSidebarSection
                resultLines={resultLinesV2}
                annotationsMatched={annotationsMatchedV2}
                blockHighlight
                showMatchesSection={false}
              />
              <div className="flex flex-row self-end gap-2">
                <button
                  type="button"
                  className="button button--secondary w-25 h-8 justify-center"
                  onClick={handleEdit}
                >
                  <span className="font-bold">Edit</span>
                </button>
                <button
                  type="button"
                  className="button button--secondary w-25 h-8 justify-center"
                  onClick={handleClear}
                >
                  <span className="font-bold">Clear</span>
                </button>
              </div>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              data-testid="test-viz-phrase-form"
              className="flex flex-col gap-4"
            >
              <div className="flex flex-col gap-1">
                <textarea
                  ref={TextAreaRef}
                  id="body"
                  data-testid="test-viz-phrase-input"
                  rows={12}
                  className="form-textarea text-body w-full transition duration-150 ease-in-out resize-none"
                  onChange={(event): void => {
                    setBody(event.target.value);
                  }}
                  value={body}
                />

                <p className="text-small">
                  <strong>Ctrl + Enter </strong>
                  to test the phrase.
                </p>
              </div>

              <button
                ref={RunButtonRef}
                type="submit"
                data-testid="run-test-button"
                className={`h-8 w-37 text-body button button--primary flex justify-center self-end ${
                  body === '' ? 'bg-opacity-25' : ''
                }`}
                disabled={body === ''}
                onClick={(): void => {
                  dispatch(setCurrentTestIdentifierRevisionId(identifierId ?? ''));
                  dispatch(setShowHighlights(true));
                }}
              >
                <span className="text-white font-bold">
                  {testSentenceLoading ? <LoadingIndicator size="5" /> : 'Run Test'}
                </span>
              </button>
            </form>
          )}
        </div>

        <div className="border-b border-litlingo-gray-4" />

        <div className="flex flex-col gap-2">
          <div className="flex">
            <span className="text-heading-3">Results</span>

            {!resultLinesV2 && (
              <span className="text-body italic text-litlingo-gray-4">
                Run your test to see the results here.
              </span>
            )}
          </div>
          {resultLinesV2 && (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col items-end self-end">
                <span>Threshold set</span>
                <div className="flex flex-row gap-1">
                  <span
                    className={`${
                      (categoryMatch?.score || 0) > Number.parseFloat(categoryThreshold)
                        ? 'bg-litlingo-highlight'
                        : ''
                    }`}
                  >
                    Staging: {categoryThreshold},
                  </span>
                  <span
                    className={`${
                      (prodCategoryMatch?.score || 0) > Number.parseFloat(prodCategoryThreshold)
                        ? 'bg-litlingo-highlight'
                        : ''
                    }`}
                  >
                    Prod: {prodCategoryThreshold}
                  </span>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                {categoryMatch && (
                  <div
                    className={`pl-1.5 flex flex-col gap-2 ${
                      selectedEnv === 'staging'
                        ? 'border-l-4 border-litlingo-secondary-100'
                        : 'border-l-4 border-transparent'
                    }`}
                  >
                    <span>
                      (Score:{categoryMatch.score}) {categoryMatch.explanation}
                    </span>
                  </div>
                )}
                {prodCategoryMatch && (
                  <div
                    className={`pl-1.5 flex flex-col gap-2 ${
                      selectedEnv === 'production'
                        ? 'border-l-4 border-litlingo-secondary-100'
                        : 'border-l-4 border-transparent'
                    }`}
                  >
                    <span>
                      (Score:{prodCategoryMatch.score} {prodCategoryMatch.explanation})
                    </span>
                  </div>
                )}
              </div>
              <a
                className="mt-2 flex flex-row items-center gap-2 self-center text-body"
                target="_blank"
                rel="noopener noreferrer"
                href={`${window.location.origin}/global/test-viz-result?channel_id=${channelId}`}
              >
                <span>{EXPAND_ICON}</span>
                <span>Language Visualizer Pop-out</span>
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TestSentenceVisualizerSidebarCategorizer;
